/**
 * Core data model for bead patterns.
 *
 * A pattern is a rectangular grid of cells. How that grid is laid out in space
 * (offsets, bead proportions, thread path) is decided entirely by the stitch —
 * see `stitches.ts`. Cell values are indices into the pattern's own palette,
 * with 0 always meaning "no bead".
 */

export type StitchId =
  | 'square'
  | 'loom'
  | 'peyote'
  | 'brick'
  | 'herringbone'
  | 'crochet';

export type BeadSizeId =
  | 'delica11'
  | 'delica10'
  | 'delica8'
  | 'round15'
  | 'round11'
  | 'round8'
  | 'round6'
  | 'square';

/** A colour available for painting. Entry index 0 in a palette is always EMPTY. */
export interface BeadColor {
  /** Stable id, e.g. "DB-0010" or "custom-3f2a". */
  id: string;
  /** Manufacturer code shown to the user, e.g. "DB0010". Empty for custom. */
  code: string;
  name: string;
  /** #rrggbb — approximate screen colour. */
  hex: string;
  brand: string;
  /** e.g. "Opaque", "Matte", "Silver-lined". Purely informational. */
  finish?: string;
  /** Symbol used in printed/black-and-white charts. Auto-assigned if absent. */
  symbol?: string;
}

export interface PatternMeta {
  author: string;
  notes: string;
  created: number;
  modified: number;
}

export interface Pattern {
  id: string;
  name: string;
  stitch: StitchId;
  beadSize: BeadSizeId;
  /** Columns. */
  width: number;
  /** Rows. */
  height: number;
  /**
   * Palette used by this pattern. Index 0 is the reserved EMPTY entry.
   * Cell values index into this array.
   */
  palette: BeadColor[];
  /** width*height cell values, row-major, top row first. */
  cells: Uint16Array;
  /** Peyote only: odd-count peyote changes the thread path and word chart. */
  oddCount?: boolean;
  /**
   * Bead crochet only: how many beads go around the rope. The grid width is
   * the circumference; each row is shifted by one bead relative to the last.
   */
  ropeShift?: number;
  meta: PatternMeta;
}

export const EMPTY = 0;

export const EMPTY_COLOR: BeadColor = {
  id: 'empty',
  code: '',
  name: 'Empty',
  hex: '#00000000',
  brand: '',
};

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Point {
  x: number;
  y: number;
}

/** A grid coordinate. col is 0-based from the left, row 0-based from the top. */
export interface Cell {
  col: number;
  row: number;
}
